<template>
  <v-card elevation="0" class="pa-0 yield-card">
    <div class="yield-header">
      <h3 class="yield-title">{{ $t('finance.loanApplications.forecastedYields') }}</h3>
    </div>

    <v-row no-gutters class="yield-row">
      <v-col cols="12" md="3" class="yield-left pt-2 pb-4 pr-3">
        <div class="graph-header">
          <div class="graph-title" style="margin-bottom: 0;">{{ $t('finance.loanApplications.yieldForecasts') }}</div>
        </div>

        <div class="forecast-row" v-for="item in yieldForecasts" :key="item.year">
          <div class="forecast-year">{{ item.year }}:</div>
          <div class="forecast-value">{{ item.value }}</div>
        </div>
      </v-col>

      <v-col cols="12" md="9" class="yield-right pt-2 pb-4 pl-3">
        <div class="graph-header graph-header-graph">
          <div class="graph-title">{{ $t('finance.loanApplications.graph') }}</div>
        </div>

        <svg
          viewBox="0 0 900 330"
          class="yield-chart"
          preserveAspectRatio="none"
          :aria-label="$t('finance.loanApplications.yieldForecastChartAria')"
        >
          <!-- Grid -->
          <g opacity="0.35">
            <line x1="95" y1="70" x2="875" y2="70" stroke="#222" />
            <line x1="95" y1="115" x2="875" y2="115" stroke="#222" />
            <line x1="95" y1="160" x2="875" y2="160" stroke="#222" />
            <line x1="95" y1="205" x2="875" y2="205" stroke="#222" />
            <line x1="95" y1="250" x2="875" y2="250" stroke="#222" />
          </g>

          <!-- Axes -->
          <line x1="95" y1="50" x2="95" y2="260" stroke="#222" stroke-width="2" />
          <line x1="95" y1="260" x2="875" y2="260" stroke="#222" stroke-width="2" />

          <!-- X tick marks -->
          <g>
            <line
              v-for="tick in xTicks"
              :key="`xt-${tick}`"
              :x1="xForYear(tick)"
              :x2="xForYear(tick)"
              y1="260"
              y2="266"
              stroke="#222"
              stroke-width="1"
            />
          </g>

          <!-- Y axis label -->
          <text x="30" y="170" transform="rotate(-90 30 170)" font-size="12" fill="#333">{{ $t('finance.loanApplications.yieldsKgAxis') }}</text>

          <!-- Y labels -->
          <text x="70" y="70" font-size="12" fill="#666">12k</text>
          <text x="70" y="115" font-size="12" fill="#666">11k</text>
          <text x="70" y="160" font-size="12" fill="#666">10k</text>
          <text x="70" y="205" font-size="12" fill="#666">9k</text>
          <text x="70" y="250" font-size="12" fill="#666">8k</text>

          <!-- X labels -->
          <g font-size="12" fill="#666">
            <text
              v-for="tick in xTicks"
              :key="`xl-${tick}`"
              :x="xForYear(tick)"
              y="290"
              text-anchor="middle"
            >
              {{ tick }}
            </text>
          </g>

          <!-- X axis label -->
          <text x="485" y="318" font-size="12" fill="#333" text-anchor="middle">{{ $t('finance.loanApplications.yearsAxisLabel') }}</text>

          <!-- Actual Yields (blue line) -->
          <polyline
            fill="none"
            stroke="#4aa3ff"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            :points="actualPointsStr"
          />

          <!-- Average Yields (brown closed line style) -->
          <polyline
            fill="none"
            stroke="#6d4c41"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            :points="averagePointsStr"
          />
          <g>
            <circle
              v-for="p in averagePoints"
              :key="`avg-${p.x}-${p.y}`"
              :cx="p.x"
              :cy="p.y"
              r="4.2"
              fill="#6d4c41"
            />
          </g>

          <!-- Predicted Future Yields (red star-ended line) -->
          <polyline
            fill="none"
            stroke="#c62828"
            stroke-width="2.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            :points="predictedPointsStr"
          />

          <!-- star markers at BOTH ends of predicted line -->
          <polygon
            v-if="predictedFirst"
            :points="predictedFirstStarPoints"
            fill="#c62828"
          />
          <polygon
            v-if="predictedLast"
            :points="predictedLastStarPoints"
            fill="#c62828"
          />
        </svg>

        <div class="legend">
          <span class="legend-label">Legend:</span>

          <div class="legend-item">
            <span class="legend-line legend-actual" />
            <span>Actual Yields</span>
          </div>

          <div class="legend-item">
            <span class="legend-predicted-marker">
              <svg class="legend-star-icon" viewBox="0 0 100 100" aria-hidden="true">
                <!-- 10-point star -->
                <polygon
                  points="50,5 61,38 95,38 66,57 77,90 50,71 23,90 34,57 5,38 39,38"
                  fill="#c62828"
                />
              </svg>
            </span>
            <span>Predicted Future Yields</span>
          </div>

          <div class="legend-item">
            <span class="legend-average-marker" />
            <span>Average Yields</span>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'YieldPredictionReportDetails',
  data() {
    return {
      chart: {
        // Dummy "API-like" series: { year, yield }.
        // Actual = past recorded yields up to current year (2026).
        // Predicted = future yields for the next 2 years (2027, 2028).
        actual: [
          { year: 1981, yield: 8200 },
          { year: 1986, yield: 9000 },
          { year: 1991, yield: 8700 },
          { year: 1996, yield: 8600 },
          { year: 2001, yield: 8800 },
          { year: 2006, yield: 9400 },
          { year: 2011, yield: 9800 },
          { year: 2016, yield: 9100 },
          { year: 2021, yield: 8700 },
          { year: 2026, yield: 9500 },
        ],
        predicted: [
          { year: 2027, yield: 10400 },
          { year: 2028, yield: 9800 },
        ],
        average: [
          { year: 1981, yield: 9000 },
          { year: 1986, yield: 9200 },
          { year: 1991, yield: 9100 },
          { year: 1996, yield: 9050 },
          { year: 2001, yield: 9150 },
          { year: 2006, yield: 9300 },
          { year: 2011, yield: 9450 },
          { year: 2016, yield: 9350 },
          { year: 2021, yield: 9250 },
          { year: 2026, yield: 9600 },
          { year: 2027, yield: 9850 },
          { year: 2028, yield: 9700 },
        ],
      },
    };
  },
  props: {
    application: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    yieldForecasts() {
      // Keep the left-side forecast list in sync with the predicted series.
      return (this.chart.predicted || []).map((p) => ({
        year: p.year,
        value: `${p.yield} kg`,
      }));
    },
    xTicks() {
      // X-axis tick labels from the design range, extended for predictions up to 2028.
      return [1981, 1986, 1991, 1996, 2001, 2006, 2011, 2016, 2021, 2026, 2028];
    },
    predictedFirst() {
      const pts = this.predictedCoordPoints;
      return pts.length ? pts[0] : null;
    },
    predictedLastStarPoints() {
      const last = this.predictedLast;
      if (!last) return '';
      return this.starPointsFor(last);
    },
    predictedFirstStarPoints() {
      const first = this.predictedFirst;
      if (!first) return '';
      return this.starPointsFor(first);
    },
    actualPointsStr() {
      return this.actualCoordPoints.map((p) => `${p.x},${p.y}`).join(' ');
    },
    averagePointsStr() {
      return this.averageCoordPoints.map((p) => `${p.x},${p.y}`).join(' ');
    },
    predictedPointsStr() {
      return this.predictedCoordPoints.map((p) => `${p.x},${p.y}`).join(' ');
    },
    predictedLast() {
      return this.predictedCoordPoints && this.predictedCoordPoints.length
        ? this.predictedCoordPoints[this.predictedCoordPoints.length - 1]
        : null;
    },
    averagePoints() {
      return this.averageCoordPoints || [];
    },

    actualCoordPoints() {
      return (this.chart.actual || []).map((p) => ({
        x: this.xForYear(p.year),
        y: this.yForYield(p.yield),
      }));
    },

    predictedCoordPoints() {
      return (this.chart.predicted || []).map((p) => ({
        x: this.xForYear(p.year),
        y: this.yForYield(p.yield),
      }));
    },

    averageCoordPoints() {
      return (this.chart.average || []).map((p) => ({
        x: this.xForYear(p.year),
        y: this.yForYield(p.yield),
      }));
    },
  },
  methods: {
    // Reduce star size by 50%:
    // Original offsets around center (x,y) were:
    // (0,-15),(7,-1),(23,-1),(11,9),(17,25),(0,15),(-17,25),(-11,9),(-23,-1),(-7,-1)
    // We scale dx,dy by 0.5.
    starPointsFor(point) {
      const x = point.x;
      const y = point.y;
      const s = 0.5;
      const offsets = [
        { dx: 0, dy: -15 },
        { dx: 7, dy: -1 },
        { dx: 23, dy: -1 },
        { dx: 11, dy: 9 },
        { dx: 17, dy: 25 },
        { dx: 0, dy: 15 },
        { dx: -17, dy: 25 },
        { dx: -11, dy: 9 },
        { dx: -23, dy: -1 },
        { dx: -7, dy: -1 },
      ];

      return offsets.map(({ dx, dy }) => `${x + dx * s},${y + dy * s}`).join(' ');
    },

    // Map a year to the SVG X coordinate.
    xForYear(year) {
      const xMinYear = 1981;
      const xMaxYear = 2028;
      const xLeft = 95;
      const xRight = 875;
      const t = (year - xMinYear) / (xMaxYear - xMinYear);
      return xLeft + t * (xRight - xLeft);
    },

    // Map a yield (kg) to the SVG Y coordinate.
    yForYield(yieldKg) {
      const yMin = 8000;
      const yMax = 12000;
      const yTopGrid = 70;
      const yBottomGrid = 250;
      const y = Math.max(yMin, Math.min(yMax, yieldKg));
      const t = (y - yMin) / (yMax - yMin);
      return yBottomGrid - t * (yBottomGrid - yTopGrid);
    },
  },
};
</script>

<style scoped>
.yield-card {
  width: 100%;
}

.yield-header {
  padding: 12px 0 10px;
}

.yield-title {
  color: #00bd73;
  font-weight: 700;
  margin: 0;
}

.yield-row {
  align-items: flex-start;
}

.section-title {
  font-weight: 600;
  color: #666;
  margin-bottom: 10px;
}

.forecast-row {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 1px;
  margin-bottom: 10px;
}

.forecast-year {
  font-size: 12px;
  font-weight: 600;
  color: #222;
  width: 35px;
}

.forecast-value {
  font-size: 12px;
  color: #333;
}

.graph-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.graph-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
  margin-bottom: 12px;
}

.graph-header-graph {
  padding-bottom: 2px;
  margin-bottom: 8px;
}

.yield-chart {
  width: 100%;
  height: 320px;
  display: block;
}

.legend {
  display: flex;
  justify-content: flex-start;
  gap: 28px;
  align-items: center;
  padding: 18px 0 0 0;
  flex-wrap: nowrap;
}

.legend-label {
  font-size: 12px;
  color: #333;
  font-weight: 600;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #222;
}

.legend-line {
  width: 36px;
  height: 2px;
  background: transparent;
  position: relative;
}

.legend-actual {
  background: #4aa3ff;
}

.legend-average {
  background: #6d4c41;
}

.legend-star {
  width: 18px;
  height: 18px;
  position: relative;
}

.legend-star-icon {
  width: 14px;
  height: 14px;
  display: inline-block;
}

.legend-predicted-marker {
  width: 36px;
  height: 18px;
  display: inline-block;
  position: relative;
}

.legend-predicted-marker::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 8px;
  height: 2px;
  background: #c62828;
  border-radius: 2px;
}

.legend-predicted-marker .legend-star-icon {
  position: absolute;
  left: 50%;
  top: 1px;
  transform: translateX(-50%);
}

.legend-average-marker {
  width: 36px;
  height: 18px;
  display: inline-block;
  position: relative;
}

.legend-average-marker::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 8px;
  height: 2px;
  background: #6d4c41;
  border-radius: 2px;
}

.legend-average-marker::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 9px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6d4c41;
  transform: translate(-50%, -50%);
}

.legend-predicted {
  background: transparent;
}

.legend-predicted::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 0;
  height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-bottom: 16px solid #c62828;
  transform: rotate(180deg) translateY(-1px);
}

@media (max-width: 1530px) {
  .yield-chart {
    height: 260px;
  }
}
</style>

